// Reglas de la pestaña Mensajes del cajón: etiquetas, filtros, permisos, la tira del contador de
// cobranza y la traducción de los codes del envío a lo que el operador tiene que hacer.
import { MessageMomentCategoryEnum } from '@fiado/type-kit/bin/messageMoments/enums/MessageMomentCategoryEnum';
import { MESSAGE_MOMENT_CATALOG } from '@fiado/type-kit/bin/messageMoments/constants/MessageMomentCatalog';
import { DeliveryChannelExtendedEnum } from '@fiado/type-kit/bin/messagesConnector/enums/DeliveryChannelExtendedEnum';
import { MessageLogStatusEnum } from '@fiado/type-kit/bin/messagesConnector/enums/MessageLogStatusEnum';
import { NotificationOriginEnum } from '@fiado/type-kit/bin/messagesConnector/enums/NotificationOriginEnum';
import { ApiError, userMessageOf } from '@/api/errors';
import { COLLECTION_QUOTA_DAYS, type CollectionQuotaResponse } from '@/api/services/loan-credit';
import type { CustomerMessageHistoryItem } from '@/api/services/messages-business/messages-business.client';
import { dayOffset } from '@/lib/dateHelpers';
import { momentNameOf } from '@/shared/lib/messageMoments/momentNames';
import type { PillTone } from '@/shared/patterns/Pills';

export { MessageMomentCategoryEnum };

/** Permiso para ver el historial de cada categoría. */
export const HISTORY_PERMISSION: Record<MessageMomentCategoryEnum, string> = {
    [MessageMomentCategoryEnum.SALE]: 'messages.history.sale.read',
    [MessageMomentCategoryEnum.CREDIT]: 'messages.history.credit.read',
};

export const CUSTOMER_MESSAGES_PERMISSIONS = {
    collectionQuotaRead: 'loan.credit.read',
    creditManualSend: 'loan.notice.manual.send',
    referralCouponResend: 'retail.promotion.coupon.resend',
    templateRead: 'messages.template.read',
} as const;

/** Momento del reenvío del código de referido. */
export const REFERRAL_COUPON_TEMPLATE_ID = 'PM-081';

/** Páginas vacías con cursor que se piden solas antes de declarar el historial vacío. */
export const MAX_EMPTY_PAGE_FOLLOWUPS = 3;

export const HISTORY_PAGE_SIZE = 20;

export { momentNameOf };

export const CHANNEL_LABEL: Record<DeliveryChannelExtendedEnum, string> = {
    [DeliveryChannelExtendedEnum.SMS]: 'SMS',
    [DeliveryChannelExtendedEnum.PUSH_NOTIFICATION]: 'Push',
    [DeliveryChannelExtendedEnum.MDM]: 'Pantalla',
    [DeliveryChannelExtendedEnum.MAIL]: 'Correo',
    [DeliveryChannelExtendedEnum.WHATSAPP]: 'WhatsApp',
};

export function channelLabelOf(channel: DeliveryChannelExtendedEnum): string {
    return CHANNEL_LABEL[channel];
}

export const STATUS_LABEL: Record<MessageLogStatusEnum, string> = {
    [MessageLogStatusEnum.CREATED]: 'En cola',
    [MessageLogStatusEnum.PENDING]: 'Pendiente',
    [MessageLogStatusEnum.VALIDATED]: 'Enviado',
    [MessageLogStatusEnum.COMPLETED]: 'Entregado',
    [MessageLogStatusEnum.REJECTED]: 'Fallido',
    [MessageLogStatusEnum.CANCEL]: 'Cancelado',
};

export const STATUS_TONE: Record<MessageLogStatusEnum, PillTone> = {
    [MessageLogStatusEnum.CREATED]: 'neutral',
    [MessageLogStatusEnum.PENDING]: 'neutral',
    [MessageLogStatusEnum.VALIDATED]: 'info',
    [MessageLogStatusEnum.COMPLETED]: 'success',
    [MessageLogStatusEnum.REJECTED]: 'danger',
    [MessageLogStatusEnum.CANCEL]: 'warning',
};

/** Estados que no llegaron al cliente: los junta el filtro «Fallidos». */
const FAILED_STATUSES: ReadonlySet<string> = new Set([
    MessageLogStatusEnum.REJECTED,
    MessageLogStatusEnum.CANCEL,
]);

export function originLabelOf(origin: NotificationOriginEnum): string {
    return origin === NotificationOriginEnum.MANUAL ? 'Manual' : 'Automático';
}

export type HistoryFilter = 'all' | 'sms' | 'push' | 'collection' | 'manual' | 'failed';

export const FILTERS_BY_CATEGORY: Record<
    MessageMomentCategoryEnum,
    readonly { key: HistoryFilter; label: string }[]
> = {
    [MessageMomentCategoryEnum.SALE]: [
        { key: 'all', label: 'Todos' },
        { key: 'sms', label: 'SMS' },
        { key: 'push', label: 'Push' },
        { key: 'failed', label: 'Fallidos' },
    ],
    [MessageMomentCategoryEnum.CREDIT]: [
        { key: 'all', label: 'Todos' },
        { key: 'collection', label: 'Solo cobro' },
        { key: 'manual', label: 'Manuales' },
        { key: 'failed', label: 'Fallidos' },
    ],
};

const FILTER_PREDICATE: Record<HistoryFilter, (item: CustomerMessageHistoryItem) => boolean> = {
    all: () => true,
    sms: (item) => item.channelType === DeliveryChannelExtendedEnum.SMS,
    push: (item) => item.channelType === DeliveryChannelExtendedEnum.PUSH_NOTIFICATION,
    collection: (item) => item.collection,
    manual: (item) => item.origin === NotificationOriginEnum.MANUAL,
    failed: (item) => FAILED_STATUSES.has(item.status),
};

export function filterHistory(
    items: readonly CustomerMessageHistoryItem[],
    filter: HistoryFilter,
): CustomerMessageHistoryItem[] {
    return items.filter(FILTER_PREDICATE[filter]);
}

/** Momentos de crédito que el operador puede mandar a mano, en el orden del catálogo. */
export const CREDIT_MANUAL_MOMENTS = MESSAGE_MOMENT_CATALOG.filter(
    (moment) => moment.category === MessageMomentCategoryEnum.CREDIT && moment.manualSend,
).map((moment) => ({
    templateId: moment.templateId,
    messageType: moment.messageType,
    name: momentNameOf(moment.templateId),
}));

export type ManualChannel =
    | DeliveryChannelExtendedEnum.SMS
    | DeliveryChannelExtendedEnum.PUSH_NOTIFICATION;

export const MANUAL_CHANNELS: readonly { value: ManualChannel; label: string }[] = [
    { value: DeliveryChannelExtendedEnum.SMS, label: 'SMS' },
    { value: DeliveryChannelExtendedEnum.PUSH_NOTIFICATION, label: 'Push' },
];

// ── Contador de cobranza ─────────────────────────────────────────────────────────────────────

export const QUOTA_CHANNELS = [
    { channel: DeliveryChannelExtendedEnum.SMS, label: 'SMS' },
    { channel: DeliveryChannelExtendedEnum.PUSH_NOTIFICATION, label: 'Push' },
    { channel: DeliveryChannelExtendedEnum.MDM, label: 'Pantalla' },
] as const;

export type QuotaCell = 'sent' | 'blocked' | 'empty';

export interface QuotaChannelView {
    channel: string;
    label: string;
    sentToday: number;
    max: number;
    used: boolean;
    /** Del día más viejo al de hoy. */
    strip: { day: string; cell: QuotaCell }[];
}

/** Una caja por canal con lo de hoy y la tira de los últimos `days` días. */
export function quotaViewsOf(quota: CollectionQuotaResponse, days: number): QuotaChannelView[] {
    const byKey = new Map(quota.days.map((d) => [`${d.channel}#${d.storeDay}`, d]));
    return QUOTA_CHANNELS.map(({ channel, label }) => {
        const strip = Array.from({ length: days }, (_, index) => {
            const day = dayOffset(quota.today, index - (days - 1));
            const entry = byKey.get(`${channel}#${day}`);
            const cell: QuotaCell =
                entry === undefined
                    ? 'empty'
                    : entry.sent > 0
                      ? 'sent'
                      : entry.blocked > 0
                        ? 'blocked'
                        : 'empty';
            return { day, cell };
        });
        const sentToday = byKey.get(`${channel}#${quota.today}`)?.sent ?? 0;
        return {
            channel,
            label,
            sentToday,
            max: quota.maxPerChannel,
            used: sentToday >= quota.maxPerChannel,
            strip,
        };
    });
}

// ── Errores ──────────────────────────────────────────────────────────────────────────────────

export const RETRY_LATER = 'No se pudo enviar; intenta de nuevo en unos minutos.';

const SEND_ERROR_BY_CODE: Record<string, string> = {
    MESSAGE_MOMENT_NOT_MANUAL: 'Este mensaje no se puede enviar a mano.',
    MESSAGE_CHANNEL_NOT_ENABLED: 'Ese canal está apagado para este mensaje en Mensajes a cliente.',
    CUSTOMER_CREDIT_NOT_FOUND: 'Este cliente no tiene un crédito al cual ligar el mensaje.',
    CUSTOMER_PHONE_MISSING:
        'El cliente no tiene teléfono registrado; agrégalo en Perfil / KYC antes de enviar.',
    CUSTOMER_PHONE_UNSUPPORTED:
        'El teléfono del cliente no sirve para enviar mensajes; corrígelo en Perfil / KYC.',
    NO_ACTIVE_REFERRAL_COUPON: 'Este cliente no tiene un código de referido vigente.',
    RETAIL_CUSTOMER_NOT_FOUND: 'No encontramos a este cliente en tu cadena.',
    REFERRAL_MESSAGE_SMS_DISABLED:
        'El SMS «Aquí está tu código» está apagado en Mensajes a cliente.',
    CUSTOMER_WITHOUT_FIADO_ACCOUNT:
        'Este cliente no tiene cuenta Fiado; no se le puede enviar su código.',
    CUSTOMER_PHONE_NOT_USABLE:
        'El teléfono del cliente no sirve para enviar SMS; corrígelo en la ficha del cliente.',
    MISSING_RETAILER_SCOPE:
        'Tu usuario no tiene una cadena asignada; pide a tu administrador que te la asigne.',
    PERMISSION_DENIED: 'No tienes permiso para enviar este mensaje.',
};

/** Lo que el operador tiene que saber de un envío fallido. Todo 5xx es «intenta más tarde». */
export function sendErrorMessageOf(error: unknown): string {
    if (error instanceof ApiError) {
        if (Object.hasOwn(SEND_ERROR_BY_CODE, error.code)) return SEND_ERROR_BY_CODE[error.code];
        if (error.httpStatus >= 500) return RETRY_LATER;
    }
    return userMessageOf(error);
}

export type HistoryFailure = 'cursor' | 'notFound' | 'forbidden' | 'unavailable' | 'other';

export function historyFailureOf(error: unknown): HistoryFailure | null {
    if (error === null || error === undefined) return null;
    if (!(error instanceof ApiError)) return 'other';
    if (error.code === 'CUSTOMER_MESSAGE_HISTORY_CURSOR_INVALID') return 'cursor';
    if (error.code === 'RETAIL_CUSTOMER_NOT_FOUND') return 'notFound';
    if (error.httpStatus === 403) return 'forbidden';
    if (error.httpStatus >= 500) return 'unavailable';
    return 'other';
}

export const HISTORY_FAILURE_COPY: Record<HistoryFailure, { title: string; body: string }> = {
    cursor: {
        title: 'La lista cambió mientras la veías',
        body: 'Vuelve a cargarla para ver los mensajes desde el más reciente.',
    },
    notFound: {
        title: 'No encontramos a este cliente',
        body: 'El cliente no existe en tu empresa, así que no tiene mensajes que mostrar.',
    },
    forbidden: {
        title: 'No tienes permiso para ver sus mensajes',
        body: 'Tu rol no incluye este historial; contacta a quien administra los roles.',
    },
    unavailable: {
        title: 'No se pudieron consultar sus mensajes',
        body: 'Un servicio no respondió. Intenta de nuevo en unos minutos.',
    },
    other: {
        title: 'No se pudieron cargar sus mensajes',
        body: 'Algo falló al leerlos. Intenta de nuevo en un momento.',
    },
};

export const COPY = {
    historyTitle: 'Mensajes enviados',
    noAccountTitle: 'Sin cuenta Fiado',
    noAccount: 'Este cliente no tiene cuenta Fiado; no hay mensajes que mostrar.',
    empty: 'Todavía no se le ha enviado ningún mensaje.',
    emptyRecent: 'No hay mensajes recientes',
    loadMoreError: 'No se pudieron cargar más mensajes; intenta de nuevo.',
    emptyFiltered: 'Ningún mensaje coincide con el filtro.',
    hiddenContent: 'Contenido oculto',
    loadMore: 'Cargar más',
    retry: 'Reintentar',
    collectionBadge: 'COBRO',
    quotaTitle: 'Cobro de hoy',
    quotaUsed: 'Usado',
    quotaAvailable: 'Disponible',
    quotaLegend: `Últimos ${String(COLLECTION_QUOTA_DAYS)} días: lleno = enviado, punteado = bloqueado por el tope.`,
    quotaChannelLabel: (channel: string) => `Cobro por ${channel}`,
    quotaStripLabel: (channel: string) => `Últimos días por ${channel}`,
    quotaError: 'No se pudo leer el contador de cobranza.',
    sendOpen: 'Enviar mensaje',
    sendClose: 'Cancelar',
    send: 'Enviar',
    sendSuccess: 'Mensaje enviado',
    templateLabel: 'Mensaje',
    channelLabel: 'Canal',
    variablesNote: 'Las variables se llenan con los datos del cliente',
    previewError: 'No se pudo cargar el texto de la plantilla. Puedes enviarlo de todos modos.',
    resendCoupon: 'Reenviar código de referido',
    resendCouponNote: 'Se le manda por SMS su código de referido vigente.',
    filtersLabel: 'Filtrar mensajes',
    listLabel: 'Mensajes del cliente',
} as const;
